import * as THREE from 'three';

class TextureManager {
    constructor() {
        this.textureLoader = new THREE.TextureLoader();
        this.textureCache = new Map();
        // Cache DOM images alongside textures to avoid re-extraction
        this.domImageCache = new Map();
        // Progress tracking
        this.progressCallback = null;
        this.loadedCount = 0;
        this.totalCount = 0;
    }
    
    // Set a callback to be called when textures load
    setProgressCallback(callback) {
        this.progressCallback = callback;
    }
    
    // Reset progress tracking
    resetProgress(totalCount = 0) {
        this.loadedCount = 0;
        this.totalCount = totalCount;
    }

    // Load texture with proper encoding and caching
    loadTextureWithProperEncoding(url) {
        // Check if the texture is already in the cache
        if (this.textureCache.has(url)) {
            return Promise.resolve(this.textureCache.get(url));
        }
        
        return new Promise((resolve, reject) => {
            this.textureLoader.load(
                url,
                (texture) => {
                    // Use modern colorSpace property instead of encoding
                    texture.colorSpace = THREE.SRGBColorSpace;
                    
                    // Store in cache for future use
                    this.textureCache.set(url, texture);
                    
                    // Also cache the DOM image for instant retrieval
                    this.cacheDOMImage(url, texture);
                    
                    // Update progress tracking
                    this.loadedCount++;
                    // console.log(`TextureManager: Loaded ${this.loadedCount}/${this.totalCount}: ${url}`);
                    if (this.progressCallback) {
                        this.progressCallback(this.loadedCount, this.totalCount);
                    }
                    
                    resolve(texture);
                },
                undefined,
                (error) => {
                    // Still count failed loads in progress
                    this.loadedCount++;
                    // console.log(`TextureManager: Failed ${this.loadedCount}/${this.totalCount}: ${url}`);
                    if (this.progressCallback) {
                        this.progressCallback(this.loadedCount, this.totalCount);
                    }
                    reject(error);
                }
            );
        });
    }

    // Cache DOM image from texture data to avoid re-extraction
    cacheDOMImage(url, texture) {
        try {
            // Get the image from the texture if it exists
            if (texture.image && texture.image instanceof HTMLImageElement) {
                // Clone the image to avoid reference issues
                const cachedImg = texture.image.cloneNode(true);
                this.domImageCache.set(url, cachedImg);
            }
        } catch (error) {
            // If extraction fails, we'll fall back to normal loading
            console.warn('Failed to cache DOM image for', url, error);
        }
    }

    // Get a cached DOM image synchronously if available
    getCachedDOMImage(url) {
        if (this.domImageCache.has(url)) {
            // Return a clone to ensure safety
            return this.domImageCache.get(url).cloneNode(true);
        }
        // Also check if we have a texture with an image we can use
        if (this.textureCache.has(url)) {
            const texture = this.textureCache.get(url);
            if (texture.image && texture.image instanceof HTMLImageElement) {
                const img = texture.image.cloneNode(true);
                // Cache it for next time
                this.domImageCache.set(url, img.cloneNode(true));
                return img;
            }
        }
        return null;
    }

    // Get a cached DOM image from a texture URL - now instant for cached textures
    getDOMImageFromTexture(url) {
        const ensureDecoded = async (el) => {
            // Hint the browser to decode immediately if possible
            try { el.decoding = 'sync'; } catch (_) {}
            if (typeof el.decode === 'function') {
                try { await el.decode(); } catch (_) {}
            } else if (!el.complete) {
                await new Promise(res => {
                    el.onload = () => res();
                    el.onerror = () => res();
                });
            }
            return el;
        };

        // First check if we have a pre-cached DOM image
        if (this.domImageCache.has(url)) {
            const cached = this.domImageCache.get(url).cloneNode(true);
            return ensureDecoded(cached);
        }
        
        // Check if we have a cached texture and can extract the image
        if (this.textureCache.has(url)) {
            const texture = this.textureCache.get(url);
            if (texture.image && texture.image instanceof HTMLImageElement) {
                const img = texture.image.cloneNode(true);
                // Cache a clone for future requests
                this.domImageCache.set(url, img.cloneNode(true));
                return ensureDecoded(img);
            }
        }
        
        // Fallback: If we don't have cached data, load both texture and image
        return new Promise((resolve, reject) => {
            this.loadTextureWithProperEncoding(url)
                .then(async (texture) => {
                    if (texture.image && texture.image instanceof HTMLImageElement) {
                        const img = texture.image.cloneNode(true);
                        this.domImageCache.set(url, img.cloneNode(true));
                        resolve(await ensureDecoded(img));
                    } else {
                        // Final fallback - create new image element
                        const img = new Image();
                        img.crossOrigin = 'anonymous';
                        img.onload = async () => {
                            this.domImageCache.set(url, img.cloneNode(true));
                            resolve(await ensureDecoded(img));
                        };
                        img.onerror = (err) => reject(err);
                        img.src = url;
                    }
                })
                .catch(err => reject(err));
        });
    }

    // Clear the texture cache
    clearCache() {
        // Dispose of all textures to free memory
        this.textureCache.forEach(texture => {
            if (texture.dispose) {
                texture.dispose();
            }
        });
        
        // Clear both caches
        this.textureCache.clear();
        this.domImageCache.clear();
    }

    // Get the raw texture loader for edge cases
    getLoader() {
        return this.textureLoader;
    }
}

// Export singleton instance
export const textureManager = new TextureManager(); 